import React, { useState, useEffect } from 'react';
import { FileText, ExternalLink, Calendar, TrendingUp, ArrowLeft, Sparkles, MessageSquare } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useToast, ToastContainer } from '../componets/Toast';
import { useNavigate } from 'react-router-dom';
import API_URL from '../config/API_URL';
import FloatingBriefingChat from '../componets/FloatingBriefingChat';

const TopicBriefings = () => {
  const [briefings, setBriefings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBriefing, setSelectedBriefing] = useState(null);
  const navigate = useNavigate();

  const [showChat, setShowChat] = useState(false);
  const [chatBriefing, setChatBriefing] = useState(null);
  const [contacts, setContacts] = useState([]);
  
  const { toasts, removeToast, showSuccess, showError } = useToast();

  const styles = {
    page: {
      padding: '2rem',
      maxWidth: '1200px',
      margin: '0 auto',
    },
    header: {
      marginBottom: '2rem',
    },
    backButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.5rem 1rem',
      border: 'none',
      background: 'transparent',
      color: '#2563eb',
      cursor: 'pointer',
      fontSize: '0.95rem',
      fontWeight: 600,
      marginBottom: '1rem',
      transition: 'all 200ms',
    },
    title: {
      fontSize: '2rem',
      fontFamily: "'Syne', sans-serif",
      fontWeight: 800,
      color: '#0f172a',
      marginBottom: '0.5rem',
    },
    subtitle: {
      color: '#475569',
      fontSize: '1rem',
    },
    briefingsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
      gap: '1.5rem',
      marginBottom: '2rem',
    },
    briefingCard: {
      background: '#ffffff',
      borderRadius: '16px',
      padding: '1.5rem',
      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
      border: '1px solid #e2e8f0',
      cursor: 'pointer',
      transition: 'all 200ms',
      position: 'relative',
    },
    briefingHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '1rem',
    },
    topicName: {
      fontSize: '1.1rem',
      fontFamily: "'Syne', sans-serif",
      fontWeight: 700,
      color: '#0f172a',
      marginBottom: '0.25rem',
    },
    briefingDate: {
      fontSize: '0.75rem',
      color: '#94a3b8',
      display: 'flex',
      alignItems: 'center',
      gap: '0.25rem',
    },
    briefingSummary: {
      fontSize: '0.875rem',
      color: '#475569',
      lineHeight: 1.6,
      marginBottom: '1rem',
      display: '-webkit-box',
      WebkitLineClamp: 3,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
    },
    keyPointsPreview: {
      fontSize: '0.875rem',
      color: '#64748b',
      fontWeight: 500,
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    unreadIndicator: {
      position: 'absolute',
      top: '1rem',
      right: '1rem',
      width: '10px',
      height: '10px',
      borderRadius: '50%',
      background: '#2563eb',
    },
    detailView: {
      background: '#ffffff',
      borderRadius: '16px',
      padding: '2rem',
      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
      border: '1px solid #e2e8f0',
      // Add margin-right when chat is open to prevent overlap
      marginRight: showChat ? '460px' : '0',
      transition: 'margin-right 300ms ease-in-out',
    },
    detailHeader: {
      marginBottom: '2rem',
      paddingBottom: '1.5rem',
      borderBottom: '2px solid #e2e8f0',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      flexWrap: 'wrap',
      gap: '1rem',
    },
    detailHeaderContent: {
      flex: 1,
      minWidth: '300px',
    },
    detailTopicName: {
      fontSize: '1.75rem',
      fontFamily: "'Syne', sans-serif",
      fontWeight: 800,
      color: '#0f172a',
      marginBottom: '0.5rem',
    },
    detailDate: {
      fontSize: '0.95rem',
      color: '#64748b',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    generateMessageButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.875rem 1.5rem',
      border: 'none',
      borderRadius: '12px',
      fontSize: '0.95rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 200ms',
      fontFamily: "'Syne', sans-serif",
      background: showChat 
        ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' 
        : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      boxShadow: showChat 
        ? '0 4px 12px rgba(16, 185, 129, 0.4)' 
        : '0 4px 12px rgba(102, 126, 234, 0.4)',
      height: 'fit-content',
    },
    section: {
      marginBottom: '2rem',
      // Make text selectable
      userSelect: 'text',
      WebkitUserSelect: 'text',
      MozUserSelect: 'text',
    },
    sectionTitle: {
      fontSize: '1.25rem',
      fontFamily: "'Syne', sans-serif",
      fontWeight: 700,
      color: '#0f172a',
      marginBottom: '1rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    summary: {
      fontSize: '1rem',
      color: '#0f172a',
      lineHeight: 1.8,
      marginBottom: '2rem',
      // Make text selectable
      userSelect: 'text',
      WebkitUserSelect: 'text',
      MozUserSelect: 'text',
    },
    keyPoints: {
      listStyle: 'none',
      padding: 0,
      margin: 0,
    },
    keyPoint: {
      fontSize: '0.95rem',
      color: '#475569',
      lineHeight: 1.6,
      marginBottom: '0.75rem',
      paddingLeft: '1.5rem',
      position: 'relative',
      // Make text selectable
      userSelect: 'text',
      WebkitUserSelect: 'text',
      MozUserSelect: 'text',
    },
    keyPointBullet: {
      position: 'absolute',
      left: 0,
      top: '0.5rem',
      width: '6px',
      height: '6px',
      borderRadius: '50%',
      background: '#2563eb',
    },
    sources: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.75rem',
    },
    sourceItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      padding: '1rem',
      background: '#f8fafc',
      borderRadius: '10px',
      border: '1px solid #e2e8f0',
      transition: 'all 200ms',
      textDecoration: 'none',
      color: 'inherit',
    },
    sourceIcon: {
      color: '#2563eb',
      flexShrink: 0,
    },
    sourceContent: {
      flex: 1,
    },
    sourceTitle: {
      fontSize: '0.95rem',
      fontWeight: 600,
      color: '#0f172a',
      marginBottom: '0.25rem',
    },
    sourceDate: {
      fontSize: '0.75rem',
      color: '#94a3b8',
    },
    analysis: {
      fontSize: '0.95rem',
      color: '#475569',
      lineHeight: 1.8,
      padding: '1.5rem',
      background: '#eff6ff',
      borderRadius: '12px',
      border: '1px solid #bfdbfe',
      // Make text selectable
      userSelect: 'text',
      WebkitUserSelect: 'text',
      MozUserSelect: 'text',
    },
    empty: {
      textAlign: 'center',
      padding: '4rem 2rem',
      color: '#475569',
      background: '#ffffff',
      borderRadius: '16px',
      border: '2px dashed #e2e8f0',
    },
    emptyIcon: {
      marginBottom: '1rem',
      opacity: 0.5,
    },
    emptyTitle: {
      fontSize: '1.5rem',
      fontFamily: "'Syne', sans-serif",
      fontWeight: 700,
      marginBottom: '0.5rem',
      color: '#0f172a',
    },
    emptyText: {
      fontSize: '1rem',
    },
  };

  useEffect(() => {
    loadBriefings();
    loadContacts();
  }, []);

  const loadBriefings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/topic-scheduler/briefings`, {
        credentials: 'include'
      });
      const data = await response.json();
      setBriefings(data.briefings || []);
    } catch (error) {
      console.error('Failed to load briefings:', error);
      showError('Failed to load briefings');
    } finally {
      setLoading(false);
    }
  };

  const loadContacts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/contacts`, {
        credentials: 'include'
      });
      const data = await response.json();
      setContacts(data.contacts || []);
    } catch (error) {
      console.error('Failed to load contacts:', error);
    }
  };

  const handleBriefingClick = async (briefing) => {
    setSelectedBriefing(briefing);
    
    // Mark as read
    if (!briefing.is_read) {
      try {
        await fetch(`${API_URL}/api/topic-scheduler/briefings/${briefing.id}/mark-read`, {
          method: 'POST',
          credentials: 'include'
        });
        
        // Update local state
        setBriefings(briefings.map(b =>
          b.id === briefing.id ? { ...b, is_read: true } : b
        ));
      } catch (error) {
        console.error('Failed to mark as read:', error);
      }
    }
  };

  const handleBack = () => {
    setSelectedBriefing(null);
    setShowChat(false); // Close chat when going back
  };

  const handleOpenChat = (briefing) => {
    setChatBriefing(briefing);
    setShowChat(true);
  };

  const handleCloseChat = () => {
    setShowChat(false);
    setChatBriefing(null);
  };

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={{ textAlign: 'center', padding: '4rem', color: '#64748b' }}>
          Loading briefings...
        </div>
      </div>
    );
  }

  if (selectedBriefing) {
    return (
      <div style={styles.page}>
        <ToastContainer toasts={toasts} removeToast={removeToast} />
        
        <button
          onClick={handleBack}
          style={styles.backButton}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateX(-4px)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateX(0)';
          }}
        >
          <ArrowLeft size={18} />
          Back to Briefings
        </button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          style={styles.detailView}
        >
          <div style={styles.detailHeader}>
            <div style={styles.detailHeaderContent}>
              <h1 style={styles.detailTopicName}>{selectedBriefing.topic_name}</h1>
              <div style={styles.detailDate}>
                <Calendar size={18} />
                {new Date(selectedBriefing.created_at).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </div>
            </div>
            
            <button
              onClick={() => showChat ? handleCloseChat() : handleOpenChat(selectedBriefing)}
              style={styles.generateMessageButton}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = showChat 
                  ? '0 6px 20px rgba(16, 185, 129, 0.5)'
                  : '0 6px 20px rgba(102, 126, 234, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = showChat 
                  ? '0 4px 12px rgba(16, 185, 129, 0.4)'
                  : '0 4px 12px rgba(102, 126, 234, 0.4)';
              }}
            >
              <MessageSquare size={20} />
              {showChat ? 'Close Chat' : 'Generate Message'}
            </button>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <FileText size={20} />
              Summary
            </h2>
            <div style={styles.summary}>{selectedBriefing.summary}</div>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <TrendingUp size={20} />
              Key Points
            </h2>
            <ul style={styles.keyPoints}>
              {selectedBriefing.key_points.map((point, index) => (
                <li key={index} style={styles.keyPoint}>
                  <span style={styles.keyPointBullet}></span>
                  {point}
                </li>
              ))}
            </ul>
          </div>

          {selectedBriefing.sources && selectedBriefing.sources.length > 0 && (
            <div style={styles.section}>
              <h2 style={styles.sectionTitle}>
                <ExternalLink size={20} />
                Sources
              </h2>
              <div style={styles.sources}>
                {selectedBriefing.sources.map((source, index) => (
                  <a
                    key={index}
                    href={source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={styles.sourceItem}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#f1f5f9';
                      e.currentTarget.style.transform = 'translateX(4px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#f8fafc';
                      e.currentTarget.style.transform = 'translateX(0)';
                    }}
                  >
                    <ExternalLink size={18} style={styles.sourceIcon} />
                    <div style={styles.sourceContent}>
                      <div style={styles.sourceTitle}>{source.title}</div>
                      {source.date && (
                        <div style={styles.sourceDate}>{source.date}</div>
                      )}
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}

          {selectedBriefing.analysis && (
            <div style={styles.section}>
              <h2 style={styles.sectionTitle}>
                <Sparkles size={20} />
                Analysis
              </h2>
              <div style={styles.analysis}>{selectedBriefing.analysis}</div>
            </div>
          )}
        </motion.div>

        {/* Floating Chat - NO AnimatePresence wrapper, NO overlay blocking briefing */}
        {showChat && chatBriefing && (
          <AnimatePresence>
            <FloatingBriefingChat
              briefing={chatBriefing}
              contacts={contacts}
              onClose={handleCloseChat}
            />
          </AnimatePresence>
        )}
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <ToastContainer toasts={toasts} removeToast={removeToast} />

      <div style={styles.header}>
        <h1 style={styles.title}>Your Briefings</h1>
        <p style={styles.subtitle}>
          AI-generated updates on your topics of interest
        </p>
      </div>

      {briefings.length === 0 ? (
        <div style={styles.empty}>
          <FileText size={48} style={styles.emptyIcon} />
          <h2 style={styles.emptyTitle}>No Briefings Yet</h2>
          <p style={styles.emptyText}>
            Your briefings will appear here once they're generated
          </p>
        </div>
      ) : (
        <div style={styles.briefingsGrid}>
          {briefings.map((briefing, index) => (
            <motion.div
              key={briefing.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              style={styles.briefingCard}
              onClick={() => handleBriefingClick(briefing)}
              whileHover={{ y: -4, boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)' }}
            >
              {!briefing.is_read && <div style={styles.unreadIndicator} />}
              
              <div style={styles.briefingHeader}>
                <div>
                  <div style={styles.topicName}>{briefing.topic_name}</div>
                  <div style={styles.briefingDate}>
                    <Calendar size={14} />
                    {new Date(briefing.created_at).toLocaleDateString()}
                  </div>
                </div>
              </div>

              <div style={styles.briefingSummary}>
                {briefing.summary}
              </div>

              <div style={styles.keyPointsPreview}>
                <TrendingUp size={16} />
                {briefing.key_points.length} key points
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TopicBriefings;